import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    siteName: 'ArqessenceShop',
    logoImage: '',
    heroImage: '',
    footer_enabled: true,
    footer_about: '',
    footer_address: '',
    footer_phone: '',
    footer_email: '',
    footer_instagram: '',
    footer_facebook: '',
    footer_linkedin: '',
    footer_youtube: '',
    footer_twitter: '',
    footer_pinterest: '',
    footer_newsletter_enabled: true,
    footer_show_contact: true,
    footer_show_links: true,
    footer_show_social: true,
    footer_show_newsletter: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  // Track changes
  useEffect(() => {
    if (!loading) {
      setHasUnsavedChanges(true);
    }
  }, [settings.logoImage, settings.heroImage, settings.siteName]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    };
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/settings`,
        getAuthHeaders()
      );
      setSettings(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validação no frontend também
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!allowedExtensions.includes(fileExtension)) {
      alert(`Formato não permitido. Use: ${allowedExtensions.join(', ')}`);
      e.target.value = ''; // Limpa o input
      return;
    }

    // Verificar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande. Tamanho máximo: 5MB');
      e.target.value = '';
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/upload-image`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSettings(prev => ({ ...prev, [imageType]: response.data.filename }));
      alert('Imagem enviada com sucesso!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao fazer upload da imagem';
      alert(errorMsg);
      console.error('Erro no upload:', error);
    } finally {
      setUploading(false);
      e.target.value = ''; // Limpa o input
    }
  };

  const handleRemoveImage = async (imageType) => {
    const filename = settings[imageType];
    if (!filename) return;

    if (!window.confirm('Tem certeza que deseja remover esta imagem?')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/delete-image/${filename}`,
        getAuthHeaders()
      );
      setSettings(prev => ({ ...prev, [imageType]: '' }));
      alert('Imagem removida com sucesso!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao remover imagem';
      alert(errorMsg);
      console.error('Erro ao remover imagem:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings`,
        settings,
        getAuthHeaders()
      );
      setHasUnsavedChanges(false);
      alert('✅ Configurações salvas com sucesso! As alterações já estão visíveis no site.');
    } catch (error) {
      alert('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Carregando configurações...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center space-x-2 hover:text-[#00A86B]"
          >
            <ArrowLeft size={20} />
            <span>Voltar ao Dashboard</span>
          </Button>
        </div>

        <Card className="p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Configurações do Site</h1>
          <p className="text-gray-600 mb-8">Personalize o logo e banner do seu site</p>

          <div className="space-y-8">
            {/* Site Name/Logo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Logo do Site (Nome ou Imagem) *
              </label>
              
              {/* Text Name */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-2">
                  Nome (Texto)
                </label>
                <Input
                  value={settings.siteName}
                  onChange={(e) => setSettings(prev => ({ ...prev, siteName: e.target.value }))}
                  placeholder="Ex: ArqessenceShop"
                  className="text-lg"
                />
              </div>

              {/* Logo Image */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">
                  Ou Logo (Imagem)
                </label>
                {settings.logoImage ? (
                  <div className="space-y-3">
                    <div className="relative inline-block">
                      <img
                        src={settings.logoImage.startsWith('http') ? settings.logoImage : getImageUrl(settings.logoImage)}
                        alt="Logo"
                        className="h-16 object-contain rounded-lg border-2 border-gray-200 bg-white p-2"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => setSettings(prev => ({ ...prev, logoImage: '' }))}
                        className="absolute -top-2 -right-2 h-6 w-6"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                        onClick={() => document.getElementById('logo-image-input').click()}
                      >
                        {uploading ? 'Enviando...' : 'Trocar Logo'}
                      </Button>
                      <input
                        id="logo-image-input"
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                        onChange={(e) => handleImageUpload(e, 'logoImage')}
                        className="hidden"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Upload Option */}
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={uploading}
                        onClick={() => document.getElementById('logo-image-input').click()}
                        className="w-full"
                      >
                        {uploading ? 'Enviando...' : '📤 Fazer Upload do Logo'}
                      </Button>
                      <input
                        id="logo-image-input"
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                        onChange={(e) => handleImageUpload(e, 'logoImage')}
                        className="hidden"
                      />
                    </div>

                    {/* URL Option */}
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-white px-2 text-gray-500">ou</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-600 mb-1">URL Pública da Imagem</label>
                      <Input
                        type="url"
                        placeholder="https://exemplo.com/logo.png"
                        value={settings.logoImage || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, logoImage: e.target.value }))}
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Cole a URL de uma imagem hospedada externamente
                      </p>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  <strong>Formatos aceitos:</strong> JPG, JPEG, PNG, GIF, WEBP, SVG (máx. 5MB para upload)
                  <br />
                  Se você adicionar uma imagem de logo, ela será usada no lugar do texto. Recomendado: altura de 40-60px
                </p>
              </div>
            </div>

            {/* Hero Banner */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Banner do Hero (Imagem de Fundo)
              </label>
              
              {settings.heroImage ? (
                <div className="relative">
                  <img
                    src={getImageUrl(settings.heroImage)}
                    alt="Hero Banner"
                    className="w-full h-64 object-cover rounded-lg border-2 border-gray-200"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleRemoveImage('heroImage')}
                    className="absolute top-4 right-4"
                  >
                    <X size={18} />
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                  <Upload size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">Nenhuma imagem de banner</p>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => document.getElementById('hero-image-input').click()}
                  >
                    {uploading ? 'Enviando...' : 'Fazer Upload'}
                  </Button>
                  <input
                    id="hero-image-input"
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                    onChange={(e) => handleImageUpload(e, 'heroImage')}
                    className="hidden"
                  />
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-2">
                <strong>Formatos aceitos:</strong> JPG, JPEG, PNG, GIF, WEBP, SVG (máx. 5MB)
                <br />
                Recomendado: 1920x600px. A imagem será exibida com qualidade total, sem transparência.
              </p>
            </div>

            {/* Footer Configuration */}
            <div className="border-t pt-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">Configuração do Rodapé</h3>
                  <p className="text-sm text-gray-600 mt-1">Personalize as informações e seções exibidas no rodapé do site</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="footer_enabled"
                    checked={settings.footer_enabled}
                    onChange={(e) => setSettings({ ...settings, footer_enabled: e.target.checked })}
                    className="w-5 h-5 text-[#00A86B] rounded focus:ring-[#00A86B]"
                  />
                  <label htmlFor="footer_enabled" className="text-sm font-medium text-gray-700">
                    Exibir Rodapé
                  </label>
                </div>
              </div>

              {settings.footer_enabled && (
                <div className="space-y-6 bg-gray-50 p-6 rounded-lg">
                  {/* Visibilidade das Seções */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Seções Visíveis</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="footer_show_social"
                          checked={settings.footer_show_social}
                          onChange={(e) => setSettings({ ...settings, footer_show_social: e.target.checked })}
                          className="w-4 h-4 text-[#00A86B] rounded"
                        />
                        <label htmlFor="footer_show_social" className="text-sm">Redes Sociais</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="footer_show_contact"
                          checked={settings.footer_show_contact}
                          onChange={(e) => setSettings({ ...settings, footer_show_contact: e.target.checked })}
                          className="w-4 h-4 text-[#00A86B] rounded"
                        />
                        <label htmlFor="footer_show_contact" className="text-sm">Contato</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="footer_show_links"
                          checked={settings.footer_show_links}
                          onChange={(e) => setSettings({ ...settings, footer_show_links: e.target.checked })}
                          className="w-4 h-4 text-[#00A86B] rounded"
                        />
                        <label htmlFor="footer_show_links" className="text-sm">Links</label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="footer_show_newsletter"
                          checked={settings.footer_show_newsletter}
                          onChange={(e) => setSettings({ ...settings, footer_show_newsletter: e.target.checked })}
                          className="w-4 h-4 text-[#00A86B] rounded"
                        />
                        <label htmlFor="footer_show_newsletter" className="text-sm">Newsletter</label>
                      </div>
                    </div>
                  </div>

                  {/* Sobre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sobre (Descrição curta)
                    </label>
                    <Input
                      type="text"
                      value={settings.footer_about}
                      onChange={(e) => setSettings({ ...settings, footer_about: e.target.value })}
                      placeholder="ArqessenceShop - Projetos Arquitetônicos de Qualidade"
                    />
                  </div>

                  {/* Informações de Contato */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Endereço
                      </label>
                      <Input
                        type="text"
                        value={settings.footer_address}
                        onChange={(e) => setSettings({ ...settings, footer_address: e.target.value })}
                        placeholder="R. Exemplo, 123, São Paulo - SP"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone
                      </label>
                      <Input
                        type="text"
                        value={settings.footer_phone}
                        onChange={(e) => setSettings({ ...settings, footer_phone: e.target.value })}
                        placeholder="+55 11 1234-5678"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={settings.footer_email}
                        onChange={(e) => setSettings({ ...settings, footer_email: e.target.value })}
                        placeholder="contato@arqessenceshop.com.br"
                      />
                    </div>
                  </div>

                  {/* Redes Sociais */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">Redes Sociais</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Instagram
                        </label>
                        <Input
                          type="url"
                          value={settings.footer_instagram}
                          onChange={(e) => setSettings({ ...settings, footer_instagram: e.target.value })}
                          placeholder="https://instagram.com/arqessenceshop"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Facebook
                        </label>
                        <Input
                          type="url"
                          value={settings.footer_facebook}
                          onChange={(e) => setSettings({ ...settings, footer_facebook: e.target.value })}
                          placeholder="https://facebook.com/arqessenceshop"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          LinkedIn
                        </label>
                        <Input
                          type="url"
                          value={settings.footer_linkedin}
                          onChange={(e) => setSettings({ ...settings, footer_linkedin: e.target.value })}
                          placeholder="https://linkedin.com/company/arqessenceshop"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          YouTube
                        </label>
                        <Input
                          type="url"
                          value={settings.footer_youtube}
                          onChange={(e) => setSettings({ ...settings, footer_youtube: e.target.value })}
                          placeholder="https://youtube.com/@arqessenceshop"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Twitter
                        </label>
                        <Input
                          type="url"
                          value={settings.footer_twitter}
                          onChange={(e) => setSettings({ ...settings, footer_twitter: e.target.value })}
                          placeholder="https://twitter.com/arqessenceshop"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pinterest
                        </label>
                        <Input
                          type="url"
                          value={settings.footer_pinterest}
                          onChange={(e) => setSettings({ ...settings, footer_pinterest: e.target.value })}
                          placeholder="https://pinterest.com/arqessenceshop"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Newsletter */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="footer_newsletter_enabled"
                      checked={settings.footer_newsletter_enabled}
                      onChange={(e) => setSettings({ ...settings, footer_newsletter_enabled: e.target.checked })}
                      className="w-4 h-4 text-[#00A86B] rounded"
                    />
                    <label htmlFor="footer_newsletter_enabled" className="text-sm font-medium text-gray-700">
                      Ativar Newsletter
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex flex-col items-end gap-2 pt-6 border-t">
              {hasUnsavedChanges && !loading && (
                <div className="text-sm text-amber-600 font-medium flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
                  Você tem alterações não salvas
                </div>
              )}
              <Button
                onClick={handleSave}
                disabled={saving}
                className={`px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all ${
                  hasUnsavedChanges 
                    ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse' 
                    : 'bg-[#00A86B] hover:bg-[#008c5a] text-white'
                }`}
              >
                {saving ? '💾 Salvando...' : hasUnsavedChanges ? '⚠️ Salvar Alterações' : '✅ Salvar Configurações'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
